import { User } from "./User";
import { getRandomId, getRandomInt } from "../util/utils";
import { getDB } from "../util/db";
import { InspectionComponent } from "./InspectionComponent";
import { Photo } from "./Photo";
import { InspectionResult } from "./InspectionResult";
import { Api } from "../util/Api";
import { Survey } from "./Survey";
import { Document, Page } from "@react-pdf/renderer";
import { InspectionPDFRenderer, styles } from "../pages/InspectionPDFRenderer";
import { InspectionPart } from "./InspectionPart";

export class Inspection {
    constructor(props) {
        this.id = `${getRandomInt()}`;
        this.surveyId = null;
		this.jobId = null;
		this.inspectorIdentifier = null;

        // Response Info
        this.inspectionComponents = {};
        this.inspectionResults = {};
		this.primaryComponentTypeId = null; // Deprecated
		this.inspectionParts = [];
		this.inspectionPhotos = []; // General photos

        // Basic Survey Data
        this.inspector = null; // User class - right now the server is just using the logged-in user
        this.companyName = null;
        this.location = null;
        this.comments = null;
		this.result = null;
		this.disposition = null;
		this.rentalMode = false;
		this.rentalDisposition = null;

        // Survey Metadata
        this.startedAt = Date.now();
        this.completedAt = null;
        this.signature = null;

        // Local metadata
        this.syncStatus = "NEW"; // NEW/SYNC/EDIT

        // Load anything requested from the constructor
        Object.assign(this, props);
    }

	async getSharedRecipients(profile) {
		let results = Api.forProfile(profile).inspectionCall(`/v2/inspections/${this.id}/shares`)
		return results;
	}

	async shareWithUser(profile, uid) {
		let results = await Api.forProfile(profile).inspectionCall(`/v2/inspections/${this.id}/shares`, "POST", {
			sharedWithUserIdentifier: uid
		});
		return results;
	}

	async unshareWithUser(profile, uid) {
		let results = await Api.forProfile(profile).inspectionCall(`/v2/inspections/${this.id}/shares/${uid}`, "DELETE");
		return results;
	}

	static generateInspectionFromProfile(profile) {
		let insp = new Inspection();
		insp.job = profile.job;
		insp.rentalMode = profile.rentalMode;
		insp.inspectorIdentifier = profile.inspectorIdentifier;
		insp.inspectorName = profile.inspectorName;
		insp.inspectorCompany = profile.inspectorCompany;
		return insp;
	}

    static async find(inspId) {
        if(inspId == null) { return null; }

        let db = await getDB();
        let inspData = await db.get("Inspections", inspId);

        // If we are using ints
        if(inspData == null) {
            inspData = await db.get("Inspections", parseInt(inspId));
        }

        if(inspData == null) { return null; }

        return Inspection.hydrate(inspData);
    }

	static async loadFromServerSearch(profile, searchParams) {
		let db = await getDB();
		let inspections = await Api.forProfile(profile).inspectionCall(`/v2/inspections?${new URLSearchParams(searchParams)}`);
		let surveyIdSet = {};
		inspections.forEach(async inspData => {
			surveyIdSet[inspData.surveyId] = true;
			let currentInsp = await db.get("Inspections", inspData.id);
			if(currentInsp == null) { // Skip ones we already have
				// Get full download from server
				let insp = await Inspection.loadInspection(profile, inspData.id);
			}
		});
	}

	static async loadFromInspectionHeader(profile, inspHeader) {
		let db = await getDB();
		let currentInsp = await db.get("Inspections", inspHeader.id);

		if(currentInsp != null) {
			return Inspection.hydrate(currentInsp);
		}

		let insp = await Inspection.loadInspection(profile, inspHeader.id);
		return insp;
	}

	// Loads inspection into the database, as well as any other needed data (i.e., survey)
	static async loadInspection(profile, inspId, sharingCode) {
		let db = await getDB();
		let path = `/v2/inspections/${encodeURIComponent(inspId)}`;
		if(!!sharingCode) {
			path = path + "?sharingCode=" + encodeURIComponent(sharingCode);
		}
		let data = await Api.forProfile(profile).inspectionCall(path);
		let insp = Inspection.hydrate(data, {networkLoaded: true, syncStatus: "READONLY"});
		await db.put("Inspections", insp);
		await Survey.ensureSurveysPresent(profile, [insp.surveyId]);

		return insp;
	}

	static async loadFromInspectionHeaders(profile, inspectionHeaders) {
		let inspList = [];
		for(const inspHeader of inspectionHeaders) {
			let insp = await Inspection.loadFromInspectionHeader(profile, inspHeader);
			inspList.push(insp);
		};
		return inspList;
	}

	static async loadAllFromLocalByJobId(jobId) {
		let db = await getDB();
		let inspections = null;
		if(!!jobId) {
			inspections = await db.getAllFromIndex("Inspections", "jobIdIndex", jobId);
		} else {
			inspections = await db.getAll("Inspections");
		}
		return inspections.map(insp => Inspection.hydrate(insp));
	}

	static async generatePdfDocumentFromInspections(profile, inspections) {
		let db = await getDB();
		let allSurveys = await db.getAll("Surveys");
		let allJobs = await db.getAll("Jobs");

		let pdfDoc = <Document>
			{inspections.map(insp => {
				return <Page size="LETTER" key={insp.id} style={styles.pageStyle}>
					<InspectionPDFRenderer key={insp.id} inspection={insp} surveys={allSurveys} jobs={allJobs} dateFormatter={profile?.dateFormatter} />
				</Page>;
			})}
		</Document>;

		return pdfDoc;
	}

	static async generatePdfDocumentFromInspectionHeaders(profile, inspectionHeaders) {
		let inspections = await Inspection.loadFromInspectionHeaders(profile, inspectionHeaders);
		return await Inspection.generatePdfDocumentFromInspections(profile, inspections);
	}

    static hydrate(jsonData, opts = {}) {
        if(jsonData == null) { return null; }

		/* ** Basic Object Hydration ** */
		// Hydrate main object
        let insp = Object.assign(new Inspection(), jsonData);

		// Hydrate inspection components
		for(const key in insp.inspectionComponents) {
			insp.inspectionComponents[key] = InspectionComponent.hydrate(insp.inspectionComponents[key]);
		}

		// Hydrate inspection results
		for(const key in insp.inspectionResults) {
			insp.inspectionResults[key] = InspectionResult.hydrate(insp.inspectionResults[key]);
		}

		// Hydrate parts
		for(const idx in insp.inspectionParts) {
			insp.inspectionParts[idx] = InspectionPart.hydrate(insp.inspectionParts[idx]);
		}

		// Hydrate inspector
        insp.inspector = User.hydrate(insp.inspector);

		/* ** Special Considerations ** */

		// Set a sync status
		if(!!opts.syncStatus) {
			insp.syncStatus = opts.syncStatus;
		}

		// Workaround for setting a primary component type
		if(opts.networkLoaded) {
			for(const key in insp.inspectionComponents) {
				if(!!insp.inspectionComponents[key].assetNumber) {
					insp.primaryComponentTypeId = insp.inspectionComponents[key].componentTypeId;
				}
			}
		}

		// Allow force-setting this as well
		if(!!opts.primaryComponentTypeId) {
			insp.primaryComponentTypeId = opts.primaryComponentTypeId;
		}

		if(!insp.componentAssetNumber && insp.assetNumber){
			insp.componentAssetNumber = insp.assetNumber;
		}

        return insp;
    }

	get displayName() {
		return Inspection.displayNameFor(this);
	}

	get sharingLink() {
		return Inspection.sharingLinkForRecord(this.id, this.sharingCode);
	}

	static sharingLinkForRecord(inspId, sharingCode) {
		return `https://vault.mcelroy.com/apps/inspections/#/inspections/shared/${encodeURIComponent(inspId)}/${encodeURIComponent(sharingCode)}`;
	}

	set job(newJob) {
		if(newJob == null) {
			this.jobId = null;
			return;
		}

		this.jobId = newJob.id;

		// Set defaults if they don't exist yet
		if(!(this.companyName)) {
			this.companyName = newJob.companyName;
		}
		if(!(this.location)) {
			this.location = newJob.location;
		}
		if(!(this.contactName)) {
			this.contactName = newJob.contactName;
		}
		if(!(this.contactPhone)) {
			this.contactPhone = newJob.contactPhone;
		}
		if(!(this.contactEmail)) {
			this.contactEmail = newJob.contactEmail;
		}
		if(!(this.contactAddress)) {
			this.contactAddress = newJob.contactAddress;
		}
	}

	// This is so the same code can be used on the bare data
	static displayNameFor(inspData) {
		let result = inspData.inspectionComponents[inspData.primaryComponentTypeId]?.assetNumber;
		if(result == null) {
			// FIXME - this is a temporary fix
			for(const comp in inspData.inspectionComponents) {
				if(inspData.inspectionComponents[comp].assetNumber != null && comp.assetNumber != null) {
					return comp.assetNumber;
				}
			}
		}
		if(result == null){
			result = inspData.componentAssetNumber;
		}
		return result;
	}

	static syncNeededFor(inspData) {
		return (inspData.syncStatus != "SAVED" && inspData.syncStatus != "READONLY" && (!!inspData.result));
	}

	get primaryComponent() {
		let data = null;
		if(!!this.primaryComponentTypeId) {
			data = this.inspectionComponents[this.primaryComponentTypeId];
		}
		if(data != null) {
			return data;
		}
		
		return {
			assetNumber: this.componentAssetNumber,
			modelNumber: this.componentModelNumber,
			serialNumber: this.componentSerialNumber,
			componentTypeId: this.componentTypeId
		};
	}

	get isReadOnly() {
		return this.syncStatus == "READONLY";
	}

	getResponseFor(qid) {
		let resp = this.inspectionResults[qid];
		if(resp == null) {
			resp = new InspectionResult();
			resp.questionId = qid;
			this.inspectionResults[qid] = resp;
		}
		return resp;
	}

    getResponseValueFor(qid) {
        return this.getResponseFor(qid).value;
    }

    setResponseValueFor(qid, response) {
        this.getResponseFor(qid).value = response;
    }

    getResponseCommentsFor(qid) {
        return this.getResponseFor(qid).comments;
    }

    setResponseCommentsFor(qid, comments) {
        this.getResponseFor(qid).comments = comments;
    }

	setInspectionComponentDetails(comp){
		this.inspectionComponents = comp;
	}

	setInspectionComponentFor(ctypeId, comp) {
		this.inspectionComponents[ctypeId] = comp;
	}

	// Updates the asset number for the inspection and its components
	setAssetNumber(newAssetNumber) {
		// Real value
		this.assetNumber = newAssetNumber;

		// Semi-legacy (old name of field but still in use)
		this.componentAssetNumber = newAssetNumber;

		// Full legacy - should probably look to remove these
		if(this.inspectionComponents){
			for(let [key, val] of Object.entries(this.inspectionComponents)){
				val.assetNumber = newAssetNumber;
				this.inspectionComponents[key] = val;
			}
		}
		
		// Full legacy - should probably look to remove these
		if(this.components){
			this.components = this.components.map(comp => {return {...comp, "assetNumber": this.assetNumber}});
		}
	}
	
	getInspectionComponentFor(ctypeId) {
		// Oldstyle
		let ic = this.inspectionComponents[ctypeId];

		if(ic != null) {
			return ic;
		}

		// Newstyle
		if(this.components != null) {
			for(const comp of this.components) {
				if(comp.componentTypeId == ctypeId) {
					return comp;
				}
			}
		}

		// Nothing found - return empty component
		ic = new InspectionComponent();
		ic.componentTypeId = ctypeId;
		this.inspectionComponents[ctypeId] = ic;
		return ic;
	}

	addResponsePhotoFor(qid, photoData) {
		let resp = this.getResponseFor(qid);
		let newPhoto = new Photo({
			id: getRandomId(),
			photoData: photoData
		});
		resp.photos = [...resp.photos, newPhoto];
		return resp.photos;
	}

	getResponsePhotosFor(qid) {
		return this.getResponseFor(qid).photos;
	}

	removeResponsePhotoFor(qid, oldPhoto) {
		let resp = this.getResponseFor(qid);
		resp.photos = resp.photos.filter(ph => oldPhoto.id != ph.id);
	}

    async sendToServer(user) {
		// Save old ID so we know what to replace in the DB
		let oldId = this.id;

		// Network Request
		let newJson = await Api.forProfile(user).inspectionCall(
			"/v2/inspections",
			"POST",
			this.getV2Json()
		);
		
		// Hydrate and mark as sync'd
		let newInspection = Inspection.hydrate(newJson, {syncStatus: "SAVED", primaryComponentTypeId: this.primaryComponentTypeId});

		// Store in database
		let db = await getDB();
		await db.put("Inspections", newInspection);

		// Remove old inspection if necessary
		if(newInspection.id != oldId) {
			console.log(`ID changed - removing ${oldId} in favor of ${newJson.id}`);
			this.id = newJson.id;
			await db.delete("Inspections", oldId);
		}

		return newInspection;
    }

	questionMetaForResponse(resp, survey, job) {
		let allQuestions = survey?.surveyQuestions?.concat(job?.jobQuestions || []) || [];
		for(const qmeta of allQuestions) {
			if(resp.questionId == qmeta.questionId) {
				return qmeta;
			}
		}
		return null;
	}

	isValidResponseForInspection(resp, survey, job) {
		let valid = false;

		// Find question - consider it valid if not found
		let qmeta = this.questionMetaForResponse(resp, survey, job);
		if(qmeta == null) {
			return true;
		}

		let q = qmeta.question;
		// These aren't questions so we can safely ignore them
		if(q.newSection){
			return true;
		}
		if(q.commentsOnly) {
			if(q.skippable) {
				valid = true; // Nothing required here
			} else {
				if(!!resp.comments) {
					valid = true;
				}
			}
		} else {
			if(!!resp.value) {
				valid = true;
			}
		}

		return valid;
	}


	hasUnansweredQuestion(survey, job) {
		for(const key in this.inspectionResults) {
			let resp = this.getResponseFor(key);
			let qmeta = this.questionMetaForResponse(resp, survey, job);
			
			if(!this.isValidResponseForInspection(resp, survey, job)) {
				return true;
			}
		}
		return false;
	}

	getInspectionErrors(survey, job) {
		let errors = [];
		if(!this.result) {
			errors.push("No result specified");
		}
		if(!this.disposition) {
			errors.push("No disposition specified")
		}
		if(this.result == "PASS") {
			let unanswered = [];
			for(const key in this.inspectionResults) {
				let resp = this.getResponseFor(key);
				if(!this.isValidResponseForInspection(resp, survey, job)) {
					let qmeta = this.questionMetaForResponse(resp, survey, job);
					unanswered.push(qmeta.question.name);
				}
			}
			if(unanswered.length > 0) {
				errors.push("All questions must be answered to pass an inspection: missing " + unanswered.join(", "));
			}
		}
		return errors;
	}

	// NOTE - prevInspData is not exactly formatted as an inspection.  Perhaps should also allow for actual inspection values as well.
	async populateMetadataFromPreviousInspectionData(prevInspData) {
		if(prevInspData == null) { return; }

		// Look up survey
		let survey = await Survey.getActiveSurveyForComponentTypeId(prevInspData.componentType.id, prevInspData.survey);
		this.surveyId = survey?.id;

		// Copy asset data
		this.componentAssetNumber = prevInspData.componentAssetNumber;
		this.assetDescription = prevInspData.assetDescription;

		// Copy component data
		for(const comp of prevInspData.components) {
			this.populateComponentData(comp)
		}
	}

	// Makes sure that each inspectionComponent also has its inspectionComponentType
	async populateCTInComponents(){
		const db = await getDB();
		for(const [key, _] of Object.entries(this.inspectionComponents)){
			let ctData = await db.get("ComponentTypes", key);
			if(!ctData || !key){
				continue;
			}
			this.inspectionComponents[key].componentType = ctData;
		}

	}

	populateComponentData(comp) {
		// Component - Legacy way
		this.inspectionComponents[comp.componentTypeId] = comp;

		// // Component - New way
		// this.componentTypeId = comp.componentTypeId;
		// this.componentAssetNumber = comp.assetNumber;
		// this.componentModelNumber = comp.modelNumber;
		// this.componentSerialNumber = comp.serialNumber;
	}

	getV2Json() {
		return this;
	}

	// Takes a header record of an asset OR component, and flattens the component types.
	// Takes the "components" array and creates the following top-level fields:
	//   * modelNumbers
	//   * serialNumbers
	//   * componentTypeNames
	static flattenComponentsFromHeader(inspHeader, ctypeLookup) {
		const comps = inspHeader.components; 
		if(comps != null) {
			let models = [];
			let serials = [];
			let ctypes = [];
			for(const comp of comps) {
				if(!!comp.modelNumber) {
					models.push(comp.modelNumber);
				}
				if(!!comp.serialNumber) {
					serials.push(comp.serialNumber);
				}
				const ctype = ctypeLookup[comp.componentTypeId];
				if(ctype == null) {
					ctypes.push("ERROR");
				} else {
					ctypes.push(ctype.name);
				}
			}
			inspHeader.modelNumbers = models.join(", ");
			inspHeader.serialNumbers = serials.join(", ");	
			inspHeader.componentTypeNames = ctypes.join(", ");
		}

		return inspHeader; // Don't actually need to return this because we are modifying in-place
	}
}